import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { ScrollAreaHorizontal } from '@neo/primitives/scroll-area'
import { humanize } from 'inflection'

import type { ReactNode } from 'react'
import { CodeBlock } from 'v2/pages/contacts/data-capture-setup/components/code-block'
import { formatPhoneNumber } from 'v2/pages/conversations/utils'
import { CollapsiblePrompt } from 'v2/shared/components/collapsible-prompt'
import { getLocalAuth } from 'v2/utils'
import { FlowNodeTypes } from '../../types'
import { ChosenPath } from '../components/chosen-path'
import { NodeExecutionDetails } from '../components/node-execution-details'
import { NodeName } from '../components/node-name'
import { useFlowRunLogsUI } from '../hooks/use-flow-run-logs-ui'
import { getPromptText, type NodeExecutedEvent } from '../types'
import { AgentPresetNode } from './components/agent-preset-node'
import { NodeExecutionHeader } from './header'

interface NonAINodeAnalysisProps {
  node: NodeExecutedEvent
}

const StartNodeVariables = ({ node }: { node: NodeExecutedEvent }) => {
  if (node.current_node_type !== FlowNodeTypes.start) return null

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem
        value="start-node-variables"
        className="rounded-lg border border-border bg-card"
      >
        <AccordionTrigger
          className="text-h4-500 text-primary no-underline hover:no-underline px-3 py-3 [&>svg]:ml-auto"
          data-testid="button-start-node-variables"
        >
          <div className="no-underline">Start Node Variables</div>
        </AccordionTrigger>
        <AccordionContent className="border-t border-border p-0">
          <ScrollAreaHorizontal className="[&_pre]:m-0 [&_pre]:rounded-none [&_pre]:border-0">
            <CodeBlock
              code={JSON.stringify(node.node_config, null, 2)}
              language="json"
              showCopyButton={false}
            />
          </ScrollAreaHorizontal>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export const getConfigMap = (node: NodeExecutedEvent, showNewUI = false) => {
  const configMap = new Map<string, string | ReactNode>()
  if (!showNewUI) {
    // Core node information
    configMap.set(
      'Current node',
      <NodeName name={node.current_node} type={node.current_node_type} key="current-node" />,
    )

    if (node.chosen_path) configMap.set('Chosen path', <ChosenPath node={node} />)
  }
  configMap.set(
    'Previous node',
    <NodeName name={node.previous_node} type={node.previous_node_type} key="previous-node" />,
  )

  // Node-specific configurations
  if (node.node_config?.configured_inputs) {
    configMap.set(
      'Configured IVR input',
      <span className="font-jetbrains text-xs-400">{node.node_config.configured_inputs}</span>,
    )
  }

  const promptText = getPromptText(node.node_config?.prompt || node.node_config?.prompts)
  if (promptText) {
    configMap.set('Configured Prompt', <CollapsiblePrompt text={promptText} />)
  }

  if (node.node_config?.queue_priority) {
    configMap.set(
      'Queue priority',
      <span className="font-jetbrains text-xs-400 tabular-nums">
        {node.node_config.queue_priority}
      </span>,
    )
  }

  if (node.node_config?.assignment_status) {
    configMap.set(
      'Assignment status',
      <span className="font-jetbrains text-xs-400">{node.node_config.assignment_status}</span>,
    )
  }

  // SMS conversation fields
  if (node.node_config?.conversation_type) {
    configMap.set('Conversation type', humanize(node.node_config.conversation_type))
  }

  if (node.node_config?.from) {
    configMap.set(
      'From',
      <span className="font-jetbrains text-xs-400 tabular-nums">
        {formatPhoneNumber(node.node_config.from)}
      </span>,
    )
  }

  if (node.node_config?.to) {
    const { to } = node.node_config
    if (to.startsWith('sip:')) configMap.set('To', getLocalAuth().name)
    else
      configMap.set(
        'To',
        <span className="font-jetbrains text-xs-400 tabular-nums">{formatPhoneNumber(to)}</span>,
      )
  }

  if (node.node_config?.concurrency_limit_applied)
    configMap.set('Status', 'Concurrency limit applied')

  if (node.node_config?.text) {
    configMap.set('Text', <CollapsiblePrompt text={node.node_config.text} />)
  }

  // Customer feedback response (only for customer feedback nodes)
  if (node.current_node_type === FlowNodeTypes.get_input) {
    const response = node.node_config?.response_recieved || 'waiting for response'
    configMap.set('Response', <CollapsiblePrompt text={response} />)
  }

  // WhatsApp buttons
  if (node.node_config?.attributes?.whatsapp_button?.body) {
    configMap.set(
      'Attributes',
      <CollapsiblePrompt
        text={JSON.stringify(node.node_config.attributes.whatsapp_button, null, 2)}
      />,
    )
  }

  // Note: execution result (HTTP response/status code, time taken) is rendered
  // separately in the collapsible NodeExecutionDetails, not in this context map.

  return configMap
}

export const NonAINodeAnalysis = ({ node }: NonAINodeAnalysisProps) => {
  const { showNewUI, isChat } = useFlowRunLogsUI()

  if (node.current_node_type === FlowNodeTypes.start) {
    // Chat only: the customer's opening message is a chunk on the Start node, so variables
    // alone would look like the bot spoke first. Gated on the FLOW's channel because start
    // and end nodes never carry node_config.channel. Skip empty chunks — `{}` is truthy.
    const startChunks = isChat
      ? (node.chunks ?? []).filter(
          (chunk) =>
            chunk.text?.trim() ||
            ('attributes' in chunk && Object.keys(chunk.attributes ?? {}).length > 0),
        )
      : []

    // No message to space under the variables block, so no wrapper. Keeps the markup every
    // other channel already had.
    if (startChunks.length === 0) return <StartNodeVariables node={node} />

    return (
      <div className="flex flex-col gap-4">
        <StartNodeVariables node={node} />
        {startChunks.map((chunk, chunkIndex) => (
          <AgentPresetNode
            key={`start-chunk-${chunk.timestamp}-${chunkIndex}`}
            node={node}
            chunk={chunk}
          />
        ))}
      </div>
    )
  }

  if (node.current_node_type === FlowNodeTypes.agent_preset_conversation) {
    // Render an AgentPresetNode for each chunk
    return (
      <div className="flex flex-col gap-4">
        {node.chunks?.map((chunk) => (
          <AgentPresetNode key={`chunk-${chunk.message}`} node={node} chunk={chunk} />
        ))}
      </div>
    )
  }

  const configMap = getConfigMap(node, showNewUI)

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <NodeExecutionHeader node={node} />
      <div className="flex flex-col gap-4 border-t border-border px-2 py-3">
        {Array.from(configMap.entries()).map(([key, value]) => {
          if (!value) return null
          return (
            <div key={key} className="flex items-start gap-4">
              <span className="font-inter text-s-400 text-secondary w-28 shrink-0">{key}:</span>
              <span className="font-inter text-s-400 text-primary min-w-0 break-words">
                {value as string}
              </span>
            </div>
          )
        })}
      </div>
      <NodeExecutionDetails node={node} />
    </div>
  )
}
